import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import { DesktopOnly } from "@coworker/apprestructured/src/shared/components/DesktopOnly";
import { FullscreenImagePopup } from "@coworker/apprestructured/src/shared/components/ImageViewer/FullscreenImagePopup/FullscreenImagePopup";
import { MobileOnly } from "@coworker/apprestructured/src/shared/components/MobileOnly";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import FixaCarousel, {
  FixaCarouselHandle,
  FixaOverflowCarousel,
} from "@coworker/apprestructured/src/shared/wrappers/FixaCarousel/FixaCarousel";
import { AddImagesModal } from "@coworker/apprestructured/src/task-management/components/AddImagesModal/AddImagesModal";
import SomethingWentWrongPrompt from "@coworker/apprestructured/src/task-management/components/SomethingWentWrongPrompt/SomethingWentWrongPrompt";
import { applyRotations } from "@coworker/apprestructured/src/task-management/util";
import {
  useImageUploader,
  useWaitForImageUpload,
} from "@coworker/apprestructured/src/uploader/useImageUploader";
import { useTranslation } from "@coworker/locales";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import styled from "styled-components";
import { MAX_IMAGES_PER_MEDIA } from "../constants";
import { imageToExistingFile } from "../Helpers/imageHelpers";
import { useImagesByMediaId } from "../hooks/useImagesByMediaId";
import { useSyncMediaImages } from "../hooks/useSyncMediaImages";
import { useWriteSignedUrls } from "../hooks/useWriteSignedUrls";
import {
  CarouselImage,
  PaddedContainer,
  ThumbnailImage,
} from "../styles/styles";
import { Media } from "../types/media";
import { AddImagesToNoImagesMedia } from "./AddImagesToNoImagesMedia";
import { MediaImagesCarouselSkeleton } from "./skeletons/MediaImagesCarouselSkeleton";

const ImageCarouselContainer = styled.div`
  position: relative;
`;

const OverlayButton = styled.div`
  position: absolute;
  bottom: 48px;
  right: 12px;
  z-index: 1;
`;
interface MediaImagesCarouselProps {
  media: Media;
  groupId?: string;
  externalAddImagesOpen?: boolean;
  onExternalAddImagesClose?: () => void;
}

export const MediaImagesCarousel = ({
  media,
  groupId,
  externalAddImagesOpen,
  onExternalAddImagesClose,
}: MediaImagesCarouselProps) => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [addImagesModalOpen, setAddImagesModalOpen] = useState(false);

  useEffect(() => {
    if (externalAddImagesOpen) {
      setAddImagesModalOpen(true);
    }
  }, [externalAddImagesOpen]);

  const [slidingIndex, setSlidingIndex] = useState(0);
  // carouselRef lets us call goToIndex() imperatively on thumbnail click.
  // This avoids ever changing the slideIndex prop, which would trigger
  // componentDidUpdate → goToIndex → smoothScroll → isScrolling=true →
  // CSS snap class removed → visual bounce on narrow (mobile) screens.
  const carouselRef = useRef<FixaCarouselHandle | null>(null);
  const pendingIndexRef = useRef<number | null>(null);
  // Wrapper ref used to scroll the thumbnail strip only when the selected
  // thumbnail is outside the currently visible area of the strip.
  const overflowCarouselRef = useRef<HTMLDivElement>(null);

  // When slidingIndex changes (swipe or thumbnail click), scroll the
  // corresponding thumbnail into view only if it is outside the visible area.
  // scrollIntoView with inline:'nearest' is a no-op when already visible, so
  // thumbnail clicks that are already on-screen are left undisturbed.
  useEffect(() => {
    const list = overflowCarouselRef.current?.querySelector('[role="list"]');
    const thumb = list?.children[slidingIndex] as HTMLElement | undefined;
    thumb?.scrollIntoView?.({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [slidingIndex]);

  const [storedImages, setStoredImages] = useState<NewOrExistingFile[]>([]);
  const [currentImages, setCurrentImages] = useState<NewOrExistingFile[]>([]);
  const { data: images, isLoading: imagesLoading } = useImagesByMediaId(
    media.id,
    groupId
  );

  useEffect(() => {
    if (!images) return;
    const existingFiles = images.map((image) => imageToExistingFile(image));
    setStoredImages(existingFiles);
    setCurrentImages([
      ...existingFiles.map((file) => ({ ...file }) as NewOrExistingFile),
    ]);
  }, [images]);

  const useWriteSignedUrlsFunc = useMemo(() => useWriteSignedUrls, []);
  const imageSize = useMemo(() => ({ maxWidthOrHeight: 1024 }), []);

  const imageUploader = useImageUploader(
    currentImages,
    imageSize,
    useWriteSignedUrlsFunc
  );

  const imageUploaderRef = useRef(imageUploader);
  imageUploaderRef.current = imageUploader;

  const { upload: uploadImages } = useWaitForImageUpload(
    () => imageUploaderRef.current
  );

  const { createUploads: imagesCreateUploads, error: uploadImagesError } =
    imageUploader;

  const { syncMediaImages, error: syncMediaImagesError } = useSyncMediaImages({
    storedImages,
    currentImages,
    imagesCreateUploads,
  });

  const syncMediaImagesRef = useRef(syncMediaImages);
  syncMediaImagesRef.current = syncMediaImages;

  const handleAddImagesModalConfirmClick = async () => {
    try {
      const needsRotation = currentImages.some((img) => img.rotation !== 0);
      if (needsRotation) {
        const rotatedImages = await applyRotations(currentImages);
        flushSync(() => {
          setCurrentImages(rotatedImages);
        });
      }
      await uploadImages();
      await syncMediaImagesRef.current(media.id, groupId);
    } catch (error) {
      reportMessageToSentry(
        "RoomSettings: failed to upload/sync media images",
        {
          mediaId: media.id,
          groupId,
          uploadImagesError,
          syncMediaImagesError,
          errorMessage: error instanceof Error ? error.message : undefined,
        },
        "error"
      );
    }
  };

  const handleVisibleSlideChange = (visibleIndices: number[]) => {
    if (visibleIndices.length > 0) {
      const indexed = visibleIndices[0] ?? 0;
      if (
        pendingIndexRef.current === null ||
        indexed === pendingIndexRef.current
      ) {
        // Either a free swipe, or the thumbnail-initiated scroll reached its
        // target. In both cases update the visible index and release the lock.
        pendingIndexRef.current = null;
        setSlidingIndex(indexed);
      }
      // else: intermediate IntersectionObserver report during thumbnail
      // animation — ignore so the thumbnail highlight does not flicker.
    }
  };

  const handleRefreshClick = () => {
    setTimeout(() => {
      window.location.reload();
    }, 0);
  };

  const showSomethingWentWrongPrompt: boolean =
    !!uploadImagesError || !!syncMediaImagesError;

  if (imagesLoading) {
    return <MediaImagesCarouselSkeleton />;
  }

  if (!images || images.length === 0) {
    return <AddImagesToNoImagesMedia mediaId={media.id} groupId={groupId} />;
  }

  return (
    <>
      <PaddedContainer $py={"0px"} $px={"16px"}>
        <ImageCarouselContainer>
          <FixaCarousel
            ref={carouselRef}
            id="imagesByMedia"
            data-testid="imagesByMedia"
            hasSlideCounter={true}
            onVisibleSlideChange={handleVisibleSlideChange}
          >
            {images.map((image, index) => {
              const imageProps = {
                src: image?.url ?? "",
                $rotation: image?.rotation || 0,
                $clickable: true as const,
                fallback: !image?.url || image.url.trim() === "",
                onClick: () => setOpenModal(true),
              };

              return (
                <Fragment key={image?.id ?? index}>
                  <DesktopOnly>
                    <CarouselImage {...imageProps} $variant="desktop" />
                  </DesktopOnly>
                  <MobileOnly>
                    <CarouselImage {...imageProps} $variant="mobile" />
                  </MobileOnly>
                </Fragment>
              );
            })}
          </FixaCarousel>
          <OverlayButton>
            <FixaButton
              type="emphasised"
              size="small"
              onClick={() => setAddImagesModalOpen(true)}
              text={t("addImagesString")}
            />
          </OverlayButton>
        </ImageCarouselContainer>
        <div ref={overflowCarouselRef}>
          <FixaOverflowCarousel
            id="thumbnailImagesByMedia"
            data-testid="thumbnailImagesByMedia"
          >
            {images.map((image, index) => {
              const imageProps = {
                src: image?.url ?? "",
                $rotation: image?.rotation || 0,
                $selected: slidingIndex === index,
                onClick: () => {
                  pendingIndexRef.current = index;
                  setSlidingIndex(index);
                  carouselRef.current?.goToIndex(index);
                },
              };

              return (
                <Fragment key={image?.id ?? index}>
                  <ThumbnailImage {...imageProps} />
                </Fragment>
              );
            })}
          </FixaOverflowCarousel>
        </div>
      </PaddedContainer>

      <FullscreenImagePopup
        images={images}
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        slideIndex={slidingIndex}
      />
      <AddImagesModal
        isOpen={addImagesModalOpen}
        images={currentImages}
        onImagesChange={setCurrentImages}
        onClose={() => {
          setAddImagesModalOpen(false);
          onExternalAddImagesClose?.();
        }}
        onConfirmClick={handleAddImagesModalConfirmClick}
        maxImages={MAX_IMAGES_PER_MEDIA}
      />
      {showSomethingWentWrongPrompt && (
        <SomethingWentWrongPrompt onRefreshClick={handleRefreshClick} />
      )}
    </>
  );
};
